import React, { useState, useEffect, useRef } from "react";
import {
  DateTimeFieldComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";
import { toUTC } from "../../utils/dateUtils";

interface DateTimeFieldProps {
  config: DateTimeFieldComponent;
  value?: string;
  onChange?: (name: string, value: string) => void;
  externalError?: string;
}

export const DateTimeField: React.FC<DateTimeFieldProps> = ({
  config,
  value = "",
  onChange,
  externalError,
}) => {
  const [error, setError] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(config.showTimePicker !== false);
  const [dateValue, setDateValue] = useState<string>("");
  const [timeValue, setTimeValue] = useState<string>("");
  const valueUpdateRef = useRef<string>("");

  // Initialize date and time from value prop
  useEffect(() => {
    if (value && value.trim() !== "") {
      let dateTime: Date;
      let needsTimeUpdate = false;
      
      // Try parsing "dd-MM-yyyy HH:mm:ss" format first
      const ddMMyyyyWithTimeMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
      if (ddMMyyyyWithTimeMatch) {
        const [, day, month, year, hours, minutes, seconds] = ddMMyyyyWithTimeMatch;
        dateTime = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
      } else {
        // Try parsing "dd-MM-yyyy" format (date only - will need to add time)
        const ddMMyyyyMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          // Use current time for date-only values
          const now = new Date();
          const hours = String(now.getHours()).padStart(2, "0");
          const minutes = String(now.getMinutes()).padStart(2, "0");
          const seconds = String(now.getSeconds()).padStart(2, "0");
          dateTime = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds}`);
          needsTimeUpdate = true;
        } else {
          // Try parsing as ISO or other date formats
          dateTime = new Date(value);
        }
      }
      
      if (!isNaN(dateTime.getTime())) {
        // Extract date part (YYYY-MM-DD for input)
        const year = dateTime.getFullYear();
        const month = String(dateTime.getMonth() + 1).padStart(2, "0");
        const day = String(dateTime.getDate()).padStart(2, "0");
        setDateValue(`${year}-${month}-${day}`);

        // Extract time part (HH:mm for time input)
        const hours = String(dateTime.getHours()).padStart(2, "0");
        const minutes = String(dateTime.getMinutes()).padStart(2, "0");
        setTimeValue(`${hours}:${minutes}`);
        
        // If value was date-only, update it to include time (only once per value)
        if (needsTimeUpdate && onChange && valueUpdateRef.current !== value) {
          const isoValue = toUTC(dateTime) || "";
          valueUpdateRef.current = value;
          // Use requestAnimationFrame to avoid infinite loops
          requestAnimationFrame(() => {
            onChange(config.name, isoValue);
          });
        } else if (!needsTimeUpdate) {
          valueUpdateRef.current = value;
        }
      } else {
        // If parsing fails, clear the values
        setDateValue("");
        setTimeValue("");
      }
    } else {
      setDateValue("");
      setTimeValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const validateField = (inputValue: string): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (!inputValue.trim()) return rule.message || "Date and time is required";
          break;
      }
    }

    // Additional date-specific validation
    if (inputValue) {
      // Parse "dd-MM-yyyy HH:mm:ss" format for validation
      let selectedDate: Date;
      const ddMMyyyyMatch = inputValue.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
      if (ddMMyyyyMatch) {
        const [, day, month, year] = ddMMyyyyMatch;
        selectedDate = new Date(`${year}-${month}-${day}`);
      } else {
        selectedDate = new Date(inputValue);
      }

      if (!isNaN(selectedDate.getTime())) {
        if (config.minDate) {
          const minDate = new Date(config.minDate);
          if (selectedDate < minDate) {
            return `Date must be after ${formatDate(config.minDate)}`;
          }
        }

        if (config.maxDate) {
          const maxDate = new Date(config.maxDate);
          if (selectedDate > maxDate) {
            return `Date must be before ${formatDate(config.maxDate)}`;
          }
        }

        if (config.maxDate == "today") {
          const maxDate = new Date(config.maxDate);
          if (selectedDate > maxDate) {
            return `Date must be before ${formatDate(config.maxDate)}`;
          }
        }

        if (config.minDate == "today") {
          const minDate = new Date(config.minDate);
          if (selectedDate < minDate) {
            return `Date must be after ${formatDate(config.minDate)}`;
          }
        }
      }
    }

    return "";
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Combine date and time into ISO 8601 UTC format
  const combineDateTime = (date: string, time: string, useCurrentSeconds: boolean = false): string => {
    if (!date) return "";

    // Ensure we always have a valid time - if not provided, use current time
    let finalTime = time;
    let seconds = 0;
    
    if (!finalTime || finalTime.trim() === "") {
      // Auto-fill with current time including seconds
      const now = new Date();
      finalTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      seconds = now.getSeconds();
    } else {
      // Extract seconds if present in time string (HH:mm:ss format)
      const timeParts = finalTime.split(":");
      if (timeParts.length === 3 && timeParts[2]) {
        seconds = parseInt(timeParts[2], 10) || 0;
        finalTime = `${timeParts[0].padStart(2, "0")}:${timeParts[1].padStart(2, "0")}`;
      } else if (timeParts.length === 2) {
        finalTime = `${timeParts[0].padStart(2, "0")}:${timeParts[1].padStart(2, "0")}`;
        seconds = useCurrentSeconds ? new Date().getSeconds() : 0;
      } else {
        // Fallback: use current time if format is invalid
        const now = new Date();
        finalTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
        seconds = now.getSeconds();
      }
    }

    // Parse date (YYYY-MM-DD format from input)
    const dateParts = date.split("-");
    if (dateParts.length !== 3) {
      return ""; // Invalid date format
    }
    
    const [year, month, day] = dateParts;
    
    // Ensure all parts are valid
    if (!year || !month || !day) {
      return "";
    }
    
    // Parse time parts
    const [hours, minutes] = finalTime.split(":").map(Number);
    
    // Build a Date object and return ISO 8601 UTC string
    const dateObj = new Date(+year, +month - 1, +day, hours, minutes, seconds);
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString();
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDateValue = e.target.value;
    setDateValue(newDateValue);

    // Always ensure we have a time value - use current time if not set
    const finalTime = timeValue || getCurrentTime();
    const useCurrentSeconds = !timeValue; // Use current seconds when auto-filling time
    
    // Only call onChange if we have a valid date
    if (newDateValue) {
      const combinedValue = combineDateTime(newDateValue, finalTime, useCurrentSeconds);
      
      // Ensure combinedValue is not empty and is a valid ISO string
      if (combinedValue && combinedValue.includes("T")) {
        if (!timeValue && newDateValue) {
          // Update time value to current time
          setTimeValue(finalTime);
        }

        const validationError = validateField(combinedValue);
        setError(validationError);
        onChange?.(config.name, combinedValue);
      }
    } else {
      // If date is cleared, clear the value
      onChange?.(config.name, "");
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTimeValue = e.target.value;
    setTimeValue(newTimeValue);

    if (dateValue && newTimeValue) {
      // When time is manually selected, use current seconds
      const combinedValue = combineDateTime(dateValue, newTimeValue, true);
      
      // Ensure combinedValue is a valid ISO string before calling onChange
      if (combinedValue && combinedValue.includes("T")) {
        const validationError = validateField(combinedValue);
        setError(validationError);
        onChange?.(config.name, combinedValue);
      }
    }
  };

  const getCurrentTime = (): string => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const getCurrentSeconds = (): string => {
    const now = new Date();
    return String(now.getSeconds()).padStart(2, "0");
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (dateValue) {
      const useCurrentSeconds = !timeValue; // Use current seconds if time wasn't set
      const combinedValue = combineDateTime(dateValue, timeValue || getCurrentTime(), useCurrentSeconds);
      const validationError = validateField(combinedValue);
      setError(validationError);
    }
  };

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    return "w-full";
  };

  const inputStyles: React.CSSProperties = {
    borderRadius: config.style?.borderRadius || 8,
    fontSize: config.style?.fontSize || 14,
    fontWeight: config.style?.fontWeight || "normal",
    ...(config.style?.textColor && !config.disabled ? { color: config.style.textColor } : {}),
    ...(config.style?.backgroundColor && !config.disabled ? { backgroundColor: config.style.backgroundColor } : {}),
    margin: config.style?.margin || 0,
    padding: config.style?.padding || undefined,
    fontFamily: "inherit",
    textAlign: config.style?.textAlign || "left",
  };

  const getMinDate = (): string | undefined => {
    if (config.minDate === "today") {
      return new Date().toISOString().split("T")[0];
    }
    return config.minDate;
  };

  const getMaxDate = (): string | undefined => {
    if (config.maxDate === "today") {
      return new Date().toISOString().split("T")[0];
    }
    return config.maxDate;
  };

  return (
    <div className={`${getWidthClass()} px-2`}>
      {config.label && (
        <label className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <div className="flex gap-2 items-start">
        {/* Date Input */}
        <div className="relative inline-block flex-1">
          <input
            type="date"
            name={config.name}
            value={dateValue}
            onChange={handleDateChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            disabled={config.disabled}
            min={getMinDate()}
            max={getMaxDate()}
            className={`w-full p-2 px-4 text-sm font-normal text-left border border-1 outline-none transition-colors duration-200 font-sans dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${
              config.disabled ? "cursor-not-allowed opacity-70 bg-gray-100 dark:bg-gray-700" : "cursor-pointer"
            } ${
              error
                ? "border-danger-500 dark:border-danger-500"
                : isFocused
                  ? "border-primary-500 dark:border-primary-400"
                  : "border-gray-300 dark:border-gray-600"
            } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
            style={inputStyles}
          />
        </div>

        {/* Time Input - Optional */}
        {showTimePicker && (
          <div className="relative inline-block flex-1">
            <input
              type="time"
              name={`${config.name}_time`}
              value={timeValue}
              onChange={handleTimeChange}
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              disabled={config.disabled}
              className={`w-full p-2 px-4 text-sm font-normal text-left border border-1 outline-none transition-colors duration-200 font-sans dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                config.disabled ? "cursor-not-allowed opacity-70 bg-gray-100 dark:bg-gray-700" : "cursor-pointer"
              } ${
                error
                  ? "border-danger-500 dark:border-danger-500"
                  : isFocused
                    ? "border-primary-500 dark:border-primary-400"
                    : "border-gray-300 dark:border-gray-600"
              } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
              style={inputStyles}
            />
          </div>
        )}
      </div>

      {(error || externalError) && (
        <div className="text-danger-500 dark:text-danger-400 text-sm mt-1 flex items-center">
          <span className="mr-1">⚠️</span>
          {error || externalError}
        </div>
      )}

      {config.helperText && !error && !externalError && (
        <div className="text-gray-500 dark:text-gray-400 text-sm mt-1">{config.helperText}</div>
      )}

      {(config.dateFormat || config.timeFormat) && (
        <div className="text-gray-400 dark:text-gray-500 text-xs mt-0.5 italic">
          Format: {config.dateFormat || "YYYY-MM-DD"} {config.timeFormat && ` ${config.timeFormat}`}
        </div>
      )}
    </div>
  );
};
